import { useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import Router from 'next/router';
import api from 'api';
import { useApiData } from 'lib/api';
import {
  Button,
  ConfirmButton,
  CopyButtonText,
  ProductIdentifier
} from 'components/common';
import { Box } from 'components/common/box';
import { ProductCustomAttributes } from 'components/common/customAttributes';
import {
  Checkbox,
  Form,
  FormGroup,
  FormSection,
  RichText,
  Select,
  Text
} from 'components/common/form';
import { UnsavedFormChanges } from 'components/common/notifications';
import {
  SettingsPicture,
  SettingsPicturePosition,
  SettingsSummaryEmail
} from '.';

import type { FormApi } from 'informed';
import type { Uppy } from '@uppy/core';
import type { Course } from 'shared-types';

interface FormValues extends Partial<Course> {
  categories?: string[];
  customAttributes?: any;
  summaryEmail?: { [key: string]: boolean };
  summaryEmailOptions?: Course['summaryEmailOptions'];
}

const SettingsCommon = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course },
    serverConfig
  } = useReduxState();

  const formApi = useRef<FormApi<FormValues>>(undefined);
  const uploader = useRef<Uppy>(undefined);

  const [state, setState] = useState({
    loading: false,
    pristine: true,
    pictureAdded: false
  });

  const { data } = useApiData({
    url: '/admin/categories-courses',
    errorMessage: messages.categoriesGetCategoriesError
  });

  const initialValues: FormValues = {
    title: course.title,
    slug: course.slug,
    picture: course.picture,
    picturePosition: course.picturePosition,
    description: course.description,
    categories: course.categories || [],
    enableChat: serverConfig.readerEnableChat ? course.enableChat : false,
    enableChatAnonymousComments: serverConfig.readerEnableChatAnonymousComments
      ? course.enableChatAnonymousComments
      : false,
    tasksAllowDownloading: course.tasksAllowDownloading,
    tasksIncludeReviews: course.tasksIncludeReviews,
    disableAllAdminAnalytics: course.disableAllAdminAnalytics,
    enableAdminAnalytics: course.enableAdminAnalytics,
    customAttributes: {},
    enableSummaryEmail: course.enableSummaryEmail,
    summaryEmailEmailList: course.summaryEmailEmailList,
    summaryEmailFrequency: course.summaryEmailFrequency,
    summaryEmail: {
      ...(course.summaryEmailOptions?.reduce((array, option) => {
        array[option] = true;
        return array;
      }, {} as Record<string, boolean>) || {})
    }
  };

  if (serverConfig.courseCustomAttributes?.length) {
    for (const attr of serverConfig.courseCustomAttributes) {
      if (course.customAttributes[attr.key] !== undefined) {
        initialValues.customAttributes[attr.key] =
          course.customAttributes[attr.key];
      } else if (attr.type === 'Number') {
        initialValues.customAttributes[attr.key] = attr.min;
      }
    }
  }

  const handleConfirm = async () => {
    const formState = formApi.current.getState();
    await submitForm(formState.values);
    return true;
  };

  const handlePictureAdded = () => {
    setState({ ...state, pictureAdded: true, pristine: false });
    dispatch.updatePageState({ hasUnsavedChanges: true });
  };

  const handlePictureRemoved = () =>
    setState({ ...state, pictureAdded: false });

  const handleSubmit = (values: FormValues) => {
    if (state.pictureAdded) {
      // upload picture before submitting form
      setState({ ...state, loading: true });
      uploader.current.upload();
    } else {
      submitForm(values);
    }
  };

  const handlePictureUpload = () => {
    const fromState = formApi.current.getState();
    submitForm(fromState.values);
  };

  const setPristine = () => setState({ ...state, pristine: false });

  const setUploader = (newUploader: Uppy) => (uploader.current = newUploader);

  const submitForm = async (values: FormValues) => {
    setState({ ...state, loading: true });

    try {
      const updateData = {
        ...values,
        ...(!values.categories ? { categories: [] } : {})
      };

      if (
        values.summaryEmail &&
        values.enableSummaryEmail &&
        values.summaryEmailEmailList.length
      ) {
        updateData.summaryEmailOptions = [];

        for (const key in values.summaryEmail as object) {
          if (values.summaryEmail[key]) {
            updateData.summaryEmailOptions.push(
              key as Course['summaryEmailOptions'][0]
            );
          }
        }
      }
      delete updateData.summaryEmail;

      await api.put(`/admin/courses/${course.id}`, updateData);

      const courses = await api.get(`/admin/courses?id=${course.id}`);

      setState({ ...state, loading: false, pristine: true });
      dispatch.updatePageState({
        course: courses.data.courses[0],
        hasUnsavedChanges: false
      });
      dispatch.addNotice({
        text: messages.settingsSavedSuccessfully,
        type: 'success'
      });

      if (course.slug !== values.slug) {
        Router.replace(
          '/admin/courses/[courseSlug]/settings',
          `/admin/courses/${values.slug}/settings`,
          { shallow: true }
        );
      }
    } catch (err) {
      setState({ ...state, loading: false });
      await dispatch.handleErrorDispatch(err);
    }
  };

  const validateSlug = async (value: string) => {
    if (!value) return messages.settingsCourseLinkEmpty;

    const slugRegex = /^[A-Za-z0-9-_]+$/;
    const valid = value.trim().match(slugRegex);
    if (!valid) return messages.settingsCourseLinkInvalid;

    try {
      await api.head(`/admin/courses?slug=${value}&id=ne|${course.id}`);
      return messages.settingsCourseLinkInUse;
    } catch (err) {
      return undefined;
    }
  };

  const validateTitle = async (value) => {
    if (!value) return messages.settingsCourseTitleEmpty;

    try {
      await api.head(
        `/admin/courses?title=${encodeURIComponent(value)}&id=ne|${course.id}`
      );
      return messages.settingsCourseTitleInUse;
    } catch (err) {
      return undefined;
    }
  };

  return (
    <div className="settings">
      <Box padding={0} title={messages.settingsCourseSettings}>
        <Form
          allowEmptyStrings={true}
          getApi={(form) => (formApi.current = form)}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {({ formState }) => (
            <>
              <FormSection maxWidth="100%">
                <UnsavedFormChanges initialValues={initialValues} />

                <FormGroup htmlFor="title" label={messages.settingsCourseTitle}>
                  <Text
                    customValidateAsync={validateTitle}
                    field="title"
                    id="title"
                    onChange={setPristine}
                  />
                </FormGroup>

                <ProductIdentifier
                  productIdentifier={course.productIdentifier}
                />

                <div className="link-container">
                  <FormGroup
                    htmlFor="link"
                    label={
                      <div className="link-label">
                        <div className="link-label-text">
                          {messages.commonLink}
                        </div>
                        <div className="link-label-copy">
                          <CopyButtonText
                            successText={messages.commonCopied}
                            text={messages.commonCopyLink}
                            value={`${serverConfig.adminUrl}/courses/${formState.values.slug}`}
                          />
                        </div>
                      </div>
                    }
                  >
                    <div className="link">
                      <div className="link-url">
                        {`${serverConfig.adminUrl}/courses/`}
                      </div>
                      <div className="link-input">
                        <Text
                          customValidateAsync={validateSlug}
                          field="slug"
                          iconStyle={{ top: 4 }}
                          id="slug"
                          onChange={setPristine}
                        />
                      </div>
                    </div>
                  </FormGroup>
                </div>

                {serverConfig.enableCoursePicture ? (
                  <FormGroup
                    htmlFor="picture"
                    label={messages.settingsCoursePicture}
                    maxWidth="none"
                  >
                    <SettingsPicture
                      field="picture"
                      id="picture"
                      messages={messages}
                      onChange={setPristine}
                      onPictureAdded={handlePictureAdded}
                      onPictureRemoved={handlePictureRemoved}
                      onPictureUpload={handlePictureUpload}
                      picturePosition={formState.values.picturePosition}
                      setUploader={setUploader}
                    />
                  </FormGroup>
                ) : null}

                {serverConfig.enableCoursePicture ? (
                  <FormGroup
                    label={messages.settingsCoursePicturePosition}
                    labelTooltip={messages.settingsCoursePicturePositionInfo}
                  >
                    <SettingsPicturePosition
                      field="picturePosition"
                      id="picture-position"
                      messages={messages}
                      onChange={setPristine}
                    />
                  </FormGroup>
                ) : null}

                <FormGroup
                  htmlFor="description"
                  label={messages.commonDescription}
                >
                  <RichText
                    autoGrow={false}
                    field="description"
                    id="description"
                    maxLength={3000}
                    onValueChange={setPristine}
                    placeholder={messages.commonDescriptionPlaceholder}
                  />
                </FormGroup>

                <FormGroup
                  htmlFor="categories"
                  label={messages.commonCategories}
                >
                  <Select
                    creatable={true}
                    field="categories"
                    id="categories"
                    isClearable={true}
                    isMulti={true}
                    isSearchable={true}
                    noOptionsMessage={messages.settingsNoCategoriesFound}
                    onChange={setPristine}
                    options={
                      data?.categories?.map((category) => ({
                        label: category.title,
                        value: category.title
                      })) || []
                    }
                    placeholder={messages.settingsAddCategory}
                  />
                </FormGroup>
              </FormSection>

              <FormSection
                maxWidth="100%"
                title={messages.settingsFunctionality}
              >
                <FormGroup label={messages.settingsChat}>
                  <Checkbox
                    disabled={!serverConfig.readerEnableChat}
                    field="enableChat"
                    id="enable-chat"
                    label={messages.settingsEnableChat}
                    onChange={setPristine}
                  />
                  <Checkbox
                    disabled={
                      !serverConfig.readerEnableChatAnonymousComments ||
                      !formState.values.enableChat
                    }
                    field="enableChatAnonymousComments"
                    id="enable-chat-anonymous-comments"
                    label={messages.settingsEnableChatAnonymousComments}
                    onChange={setPristine}
                    style={{ marginTop: 5 }}
                  />
                </FormGroup>

                <FormGroup
                  label={messages.courseAnalytics}
                  labelHelpText={messages.courseAnalyticsHelpText}
                >
                  <Checkbox
                    disabled={formState.values.disableAllAdminAnalytics}
                    field="enableAdminAnalytics"
                    id="enable-admin-analytics"
                    label={messages.settingsEnableAdminAnalytics}
                    onChange={setPristine}
                  />

                  <Checkbox
                    field="disableAllAdminAnalytics"
                    id="disable-all-admin-analytics"
                    label={messages.settingsDisableAllAdminAnalytics}
                    labelHelpText={
                      messages.settingsDisableAllAdminAnalyticsInfo
                    }
                    onChange={() => {
                      formApi.current.setValue('enableAdminAnalytics', false);
                      setPristine();
                    }}
                    style={{ marginTop: 5 }}
                  />
                </FormGroup>

                <FormGroup label={messages.commonTaskAnswers}>
                  <Checkbox
                    field="tasksAllowDownloading"
                    id="tasks-allow-downloading"
                    label={messages.settingsTasksAllowDownloading}
                    onChange={setPristine}
                  />
                  <Checkbox
                    field="tasksIncludeReviews"
                    id="tasks-include-reviews"
                    label={messages.settingsTasksIncludeReviews}
                    onChange={setPristine}
                    style={{ marginTop: 5 }}
                  />
                </FormGroup>
              </FormSection>

              <FormSection
                maxWidth="100%"
                title={messages.settingsSummaryEmail}
                helpText={messages.settingsSummaryEmailHelpText}
              >
                <SettingsSummaryEmail onChange={setPristine} />
              </FormSection>

              {serverConfig.courseCustomAttributes?.length ? (
                <FormSection
                  maxWidth="100%"
                  title={messages.settingsAdditionalSettings}
                >
                  <div className="custom-attribute-settings">
                    <ProductCustomAttributes
                      attributes={course.customAttributes}
                      onChange={setPristine}
                      references={{
                        expiration:
                          formState.values?.completionExpirationValue ||
                          course.completionExpirationValue,
                        workload:
                          formState.values?.completionWorkloadValue ||
                          course.completionWorkloadValue
                      }}
                      type="course"
                    />
                  </div>
                </FormSection>
              ) : null}

              <FormSection maxWidth="100%">
                <FormGroup>
                  {formState.values.slug !== initialValues.slug ? (
                    <ConfirmButton
                      actionText={messages.commonSaveChanges}
                      buttonText={messages.commonSaveChanges}
                      category="success"
                      confirmText={messages.settingsSaveSlugConfirm}
                      confirmTitle={messages.commonSaveChanges}
                      disabled={state.pristine || formState.invalid}
                      onConfirm={handleConfirm}
                    />
                  ) : (
                    <Button
                      category="success"
                      disabled={state.pristine || formState.invalid}
                      loading={state.loading}
                      text={messages.commonSaveChanges}
                      type="submit"
                    />
                  )}
                </FormGroup>
              </FormSection>
            </>
          )}
        </Form>
      </Box>

      <style jsx>{`
        .link-container {
          margin-top: 18px;
        }
        .link-container :global(.form-group .label) {
          display: block;
          max-width: 540px;
        }
        .link {
          display: flex;
          position: relative;
        }
        .link-label {
          display: flex;
        }
        .link-label-text {
          flex: 1 1 auto;
        }
        .link-label-copy {
          flex: 0 1 auto;
        }
        .link-url {
          color: #888;
          font-size: 1.4rem;
          padding: 4px 0 1px;
        }
        .link-input {
          flex-grow: 1;
        }
        .link-input :global(input) {
          font-size: 1.4rem;
          padding: 2px 25px 2px 3px;
        }
        .link-input :global(.icon) {
          top: 5px;
          right: 7px;
        }
      `}</style>
    </div>
  );
};

export default SettingsCommon;
